import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import { AUTOCOMPLETER_STATE_SLICE_KEY } from 'SearchApp/ts/features/base-autocomplete/state/autocompleter.reducer';

import { CategoriesAutocompleteResults } from 'SearchApp/ts/types/categories-autocomplete-results.type';
import { AutocompleteStore } from 'SearchApp/ts/features/base-autocomplete/state/interfaces/autocomplete-store.interface';
import { AutocompleteResult } from 'SearchApp/ts/interfaces/autocomplete-result.interface';

import { UrlsService } from 'Angular/services/core/urls.service';

import { SearchResultsBaseDirective } from 'SearchApp/ts/features/base-autocomplete/directives/search-results.directive';

/**
 *
 * Categories Result Component
 *
 * This component is responsible for:
 *   1. Displaying results returned from the backend
 *   2. Handling when a result is clicked and hovered
 *
 * Input Properties:
 *   showMoreResultsUrl: Whether to show the more results icon
 *   dropdownMenuClass: Add class to dropdown component
 *   globalSearch: is global search (top nav search)
 *
 */

@Component({ standalone: false, selector: 'ycn-categories-results', templateUrl: './categories-results.template.html' })
export class CategoriesResultsComponent extends SearchResultsBaseDirective implements OnDestroy, OnInit {
    @Input() showMoreResultsUrl?: boolean;

    @Input() dropdownMenuClass?: string;

    @Input() globalSearch: boolean = false;

    flatResults: AutocompleteResult[] = [];

    results: CategoriesAutocompleteResults = {};

    selectedIndex: number = -1;

    showResults: boolean = false;

    private storeSubscription: Subscription;

    constructor(
        private changeDetector: ChangeDetectorRef,
        private eltRef: ElementRef,
        urlsService: UrlsService,
        readonly store: Store<{ autocompleter: AutocompleteStore }>
    ) {
        super(urlsService, store);
        this.storeSubscription = this.store.select(AUTOCOMPLETER_STATE_SLICE_KEY).subscribe(this.mapStateToComponent);
    }

    ngOnInit(): void {
        if (this.dropdownMenuClass) this.eltRef.nativeElement.classList.add(this.dropdownMenuClass);
    }

    ngOnDestroy(): void {
        this.storeSubscription.unsubscribe();
    }

    mapStateToComponent = (state: AutocompleteStore): void => {
        const autocompleterState = state[this.autocompleterId];
        if (!autocompleterState) {
            return;
        }

        const { inputText, categoriesResults, results, selectedIndex, showResults, extraLinks } = autocompleterState;

        this.inputText = inputText;
        this.results = categoriesResults;
        this.flatResults = results;
        this.selectedIndex = selectedIndex;
        this.showResults = showResults;

        const detectWhenGlobalNoResults = this.globalSearch && this.inputText.trim() !== '' && !this.showResults;

        // Disable change detection when results are hidden
        if (this.showResults || extraLinks.length > 0 || detectWhenGlobalNoResults) {
            this.changeDetector.reattach();
            this.changeDetector.detectChanges();
        } else {
            this.changeDetector.detectChanges();
            this.changeDetector.detach();
        }
    };

    setSelectedIndexOnMouseEnter(result: AutocompleteResult): void {
        // changes selected index on mouseover so highlighted item can be submitted via enter or submit.
        const selectedIndex = this.flatResults.indexOf(result);
        this.setSelectedIndex(selectedIndex);
    }
}
